// Web photo-status mapping.
//
// Goal: keep the database enum out of the user-facing layer, and
// keep Mobile + Web labels consistent for the same semantic state.
//
// Architecture (matches the diagnostic report's three-layer design):
//
//   Layer 1 — DB enum (unchanged):
//     'pending' | 'uploading' | 'uploaded' | 'failed' | 'deleted'
//
//   Layer 2 — UI semantic (defined here, platform-agnostic):
//     pending  → 'pending'   (queued locally, no upload-url yet)
//     uploading → 'syncing'   (R2 PUT in flight)
//     uploaded → 'synced'    (R2 head succeeded + finalize committed)
//     failed    → 'failed'    (terminal, retry available)
//     deleted   → 'deleted'   (soft-delete tombstone)
//
//   Layer 3 — Display label:
//     Provided per platform. Mobile has full l10n (app_en.arb);
//     Web currently has no i18n layer, so this module returns static
//     English strings that match Mobile's English arb. When Web
//     grows an i18n layer, only this module needs to change.
//
// Rationale for the semantic layer:
//   - Decouples the wire enum from the display string. Future enum
//     additions (e.g. 'paused', 'expired') only need a new mapping
//     here; the display layer is untouched.
//   - Lets the same vocabulary as the report-level sync status
//     (statusSyncedSemantic etc. in Mobile's app_en.arb) be reused
//     across surfaces.
//   - Keeps the DB enum as a contract and the display label as a
//     leaf, so admin/email/share banners can subscribe to the
//     semantic layer without duplicating the mapping.
//
// Deleted-state handling:
//   The gallery presentation layer should filter out `deleted` rows
//   (matching Mobile's watchPhotos filter). The semantic mapping
//   still resolves `deleted` to the `deleted` UI status so any
//   non-gallery surface (e.g. an audit log) can decide its own
//   policy without consulting the DB enum directly.
import type { ReportPhotoJson } from '@/api/types';

export type PhotoUploadStatus = ReportPhotoJson['upload_status'];

/**
 * UI-facing semantic states. Distinct from the wire enum so the
 * rendering layer never reads a DB enum value directly.
 */
export type PhotoUiStatus = 'pending' | 'syncing' | 'synced' | 'failed' | 'deleted';

/**
 * Wire → semantic mapping. Pure function, exhaustive over the five
 * known states. Unknown values (e.g. an enum added server-side
 * before this client ships) fall through to 'pending' so the
 * rendering layer never crashes on a missing branch; a future
 * version of this module can surface them explicitly.
 */
export function photoUiStatusFor(status: PhotoUploadStatus): PhotoUiStatus {
  switch (status) {
    case 'pending':
      return 'pending';
    case 'uploading':
      return 'syncing';
    case 'uploaded':
      return 'synced';
    case 'failed':
      return 'failed';
    case 'deleted':
      return 'deleted';
    default:
      return 'pending';
  }
}

/**
 * Semantic → display label.
 *
 * Aligned with Mobile's English app_en.arb keys:
 *   photosStatusPending   = "Waiting to upload"
 *   photosStatusUploading = "Uploading…"
 *   photosStatusUploaded  = "In cloud"
 *   photosStatusFailed    = "Upload failed"
 *   photosStatusDeleted   = "Deleted"
 *
 * Web has no i18n framework yet, so the labels are inlined. When
 * Web grows one, swap the table for a localised lookup; the rest
 * of the rendering layer is unaffected.
 */
export const PHOTO_STATUS_LABEL: Record<PhotoUiStatus, string> = {
  pending: 'Waiting to upload',
  syncing: 'Uploading…',
  synced: 'In cloud',
  failed: 'Upload failed',
  deleted: 'Deleted',
};

/**
 * Convenience helper used by every consumer that currently writes
 * `{photo.upload_status}` — turn the wire enum straight into the
 * user-facing label. Equivalent to:
 *   PHOTO_STATUS_LABEL[photoUiStatusFor(photo.upload_status)]
 */
export function photoStatusLabel(photo: { upload_status: PhotoUploadStatus }): string {
  return PHOTO_STATUS_LABEL[photoUiStatusFor(photo.upload_status)];
}

/**
 * True when the photo should be rendered in the normal gallery
 * (Detail / Photos screen). Mirrors Mobile's `watchPhotos` filter,
 * which hides `deleted` rows so tombstones never appear in the
 * gallery. Centralised here so every Web surface stays consistent.
 */
export function isVisiblePhoto(photo: { upload_status: PhotoUploadStatus }): boolean {
  return photo.upload_status !== 'deleted';
}
